// TODO: refactor this leviathan  ->  https://secondstep.atlassian.net/browse/LEARN-4251

import React, { useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { AddIcon, ContainedButton } from 'secondstep-components'
import { toKebabCase } from 'utils/stringHelpers'
import { maxClasses } from 'config/constants'
import { INITIAL_COURSE } from 'experienceComponents/CreateInstanceShared/constants.js'
import {
  getLessonsLink,
  checkLimitReached,
  getInitialSelectedSchool,
} from './data'

import { StyledDropdownMenu, TextButton } from './component.styles'
import ModalRouter from './modalRouter'

// Import state management functions and hooks
import { useCreateCourseHandlers } from './stateManagement'
import { useSideEffects } from './useSideEffects'

const CreateCourse = props => {
  // Initialize state
  const [state, setState] = useState(() => {
    try {
      return {
        isFormValid: false,
        inputErrors: {
          className: {
            errors: null,
          },
          selectedGrade: '',
        },
        courseInstanceName: props.courseInstanceName || '',
        sitesWithGrades: [],
        modalContent: null,
        modalFlow: null,
        selectedCourse: INITIAL_COURSE,
        selectedSchool: getInitialSelectedSchool(
          props,
          {}, // filterConfig not needed for classes
        ),
        submitAttempts: 0,
      }
    } catch (error) {
      console.error('Error initializing state:', error)
      return {
        isFormValid: false,
        inputErrors: {
          className: { errors: null },
          selectedGrade: '',
        },
        courseInstanceName: props.courseInstanceName || '',
        sitesWithGrades: [],
        modalContent: null,
        modalFlow: null,
        selectedCourse: INITIAL_COURSE,
        selectedSchool: null,
        submitAttempts: 0,
      }
    }
  })

  // TODO: refactor this to not need so many side effects. This is just the initial version. Need to rethink ordering of events to avoid so many updates
  // Ref to track side effects
  const sideEffectsRef = useRef({})

  // Create state updater that handles side effects
  const updateState = useCallback((newState, sideEffects = {}) => {
    setState(newState)
    sideEffectsRef.current = sideEffects
  }, [])

  // Get all handlers from the custom hook
  const {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    goBackOneModalCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    handleSaveEditCallback,
    handleViewClassCallback,
    handleCancelCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
    handleDeleteCallback,
    validateFormCallback,
  } = useCreateCourseHandlers(state, props, updateState)

  // Allow parent to programmatically open the Create modal without clicking a button
  useEffect(() => {
    if (props.autoOpen) {
      handleCreateCallback()
    }
  }, [props.autoOpen, handleCreateCallback])

  useSideEffects(
    sideEffectsRef,
    state,
    props,
    handleSiteChangeCallback,
    handleGradeChangeCallback,
    validateFormCallback,
  )

  // Destructure state for easier access
  const {
    courseInstanceName,
    inputErrors,
    isFormValid,
    sitesWithGrades,
    modalContent,
    selectedCourse,
    selectedSchool,
    modalFlow,
    error,
    selectedGrade,
  } = state || {}

  const { course, isClassCardMenu, className } = props
  const { instance } = course

  return (
    <div className={className}>
      {!props.hideLauncher &&
        !props.edit &&
        !checkLimitReached(props.pathName, props.lms, maxClasses) &&
        !props.emptyClasses && (
          <TextButton
            dataTestId={'button-create'}
            icon={<AddIcon />}
            label="Create Class"
            onClick={handleCreateCallback}
            ref={props.forwardedRef}
          />
        )}
      {!props.hideLauncher && props.emptyClasses && (
        <ContainedButton
          dataTestId={'button-create'}
          onClick={handleCreateCallback}
          ref={props.forwardedRef}
        >
          {'Create Class'}
        </ContainedButton>
      )}
      {props.edit && (
        <StyledDropdownMenu
          courseInstanceName={courseInstanceName}
          dataTestId={`dropdown-menu-button-${toKebabCase(courseInstanceName)}`}
          handleDeleteSubmit={handleDeleteSubmitCallback}
          handleEditSubmit={handleEditSubmitCallback}
          instance={instance}
          isClassCardMenu={isClassCardMenu}
          ref={props.forwardedRef}
        />
      )}
      <ModalRouter
        contentfulCourses={props.contentfulCourses}
        courseInstanceName={courseInstanceName}
        currentSite={props.currentSite}
        error={error}
        getLessonsLink={() => getLessonsLink(props.pathName)}
        goBackOneModal={goBackOneModalCallback}
        handleCancel={handleCancelCallback}
        handleClassNameChange={handleClassNameChangeCallback}
        handleCreate={handleCreateCallback}
        handleDelete={handleDeleteCallback}
        handleGradeChange={handleGradeChangeCallback}
        handleSaveEdit={handleSaveEditCallback}
        handleSiteChange={handleSiteChangeCallback}
        handleSubmit={handleSubmitCallback}
        handleViewClass={handleViewClassCallback}
        inputErrors={inputErrors}
        isClassCardMenu={isClassCardMenu}
        isFormValid={isFormValid}
        limitReached={checkLimitReached(props.pathName, props.lms, maxClasses)}
        lms={props.lms}
        modalContent={modalContent}
        modalFlow={modalFlow}
        navigationEvent={props.navigationEvent}
        redirectToMyClasses={props.redirectToMyClasses}
        resetState={resetStateCallback}
        selectedCourse={selectedCourse}
        selectedGrade={selectedGrade}
        selectedSchool={selectedSchool}
        sitesWithGrades={sitesWithGrades}
      />
    </div>
  )
}

CreateCourse.propTypes = {
  autoOpen: PropTypes.bool,
  className: PropTypes.string,
  classesUrl: PropTypes.string,
  contentfulCourses: PropTypes.array,
  course: PropTypes.object,
  courseInstanceName: PropTypes.string,
  createCourseInstanceV2ProgramFlex: PropTypes.func.isRequired,
  currentSite: PropTypes.shape({
    isSetup: PropTypes.bool,
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteName: PropTypes.string,
  }),
  deleteCourseInstance: PropTypes.func.isRequired,
  deletingCourseCreatedStatus: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  emptyClasses: PropTypes.bool,
  fetchInstructorData: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideLauncher: PropTypes.bool,
  isClassCardMenu: PropTypes.bool,
  isCreated: PropTypes.bool,
  limitReached: PropTypes.bool,
  lms: PropTypes.object.isRequired,
  navigationEvent: PropTypes.func,
  pathName: PropTypes.string,
  redirectToMyClasses: PropTypes.func,
  setFocus: PropTypes.func,
  updateCourseInstance: PropTypes.func.isRequired,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

CreateCourse.defaultProps = {
  autoOpen: false,
  hideLauncher: false,
  course: {},
  createCourseInstanceV2ProgramFlex: () => {},
  deletingCourseCreatedStatus: () => {},
}

export default CreateCourse
